import React, { useState, useEffect, useRef, useCallback } from "react";
import { Section, ToolbarButton } from "./UI";
import { LeadCard } from "./LeadCard";
import { MultiSelect } from "./MultiSelect";
import { leadsService } from "../services/leadsService";
import { adminService } from "../services/adminService";
import { eventsService } from "../services/eventsService";
import { leadTypes, commissionModels, worksWithUsOptions, trafficSources, LEAD_STATUSES, LEAD_PRIORITIES } from "../constants/leads";

export function AllLeads({ setView, toast }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [managers, setManagers] = useState([]);
  const [events, setEvents] = useState([]);
  
  // Фильтры
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedLeadTypes, setSelectedLeadTypes] = useState([]);
  const [selectedTrafficSources, setSelectedTrafficSources] = useState([]);
  const [selectedWorksWithUs, setSelectedWorksWithUs] = useState([]);
  const [selectedCommissionModels, setSelectedCommissionModels] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Пагинация
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 50;
  
  const scrollContainerRef = useRef(null);

  // Загрузка менеджеров и событий при монтировании
  useEffect(() => {
    loadManagers();
    loadEvents();
  }, []);

  const loadManagers = async () => {
    try {
      const data = await adminService.getManagers();
      setManagers(data);
    } catch (error) {
      console.error("Ошибка загрузки менеджеров:", error);
      toast.error("Не удалось загрузить список менеджеров");
    }
  };

  const loadEvents = async () => {
    try {
      const data = await eventsService.getEventsWithLeads();
      setEvents(data);
    } catch (error) {
      console.error("Ошибка загрузки событий:", error);
      toast.error("Не удалось загрузить список событий");
    }
  };

  const loadLeads = async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setLeads([]);
      setOffset(0);
    }

    try {
      const params = {
        limit,
        offset: loadMore ? offset : 0,
      };

      // Применяем фильтры
      if (selectedEvents.length > 0 && selectedEvents.length < events.length) {
        params.event = selectedEvents[0]; // Backend expects single event
      }
      if (selectedLeadTypes.length > 0 && selectedLeadTypes.length < leadTypes.length) {
        params.lead_type = selectedLeadTypes[0]; // Backend expects single type
      }
      if (selectedTrafficSources.length > 0 && selectedTrafficSources.length < trafficSources.length) {
        params.traffic_source = selectedTrafficSources[0];
      }
      if (selectedWorksWithUs.length > 0 && selectedWorksWithUs.length < worksWithUsOptions.length) {
        params.works_with_us = selectedWorksWithUs[0];
      }
      if (selectedCommissionModels.length > 0 && selectedCommissionModels.length < commissionModels.length) {
        params.commission_model = selectedCommissionModels[0];
      }
      if (selectedManagers.length > 0 && selectedManagers.length < managers.length) {
        params.manager_telegram_ids = selectedManagers.join(',');
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const data = await leadsService.getAllLeads(params);

      if (loadMore) {
        setLeads(prev => [...prev, ...data.items]);
      } else {
        setLeads(data.items);
      }

      setTotal(data.total);
      setOffset(loadMore ? offset + limit : limit);
      setHasMore((loadMore ? offset + limit : limit) < data.total);
    } catch (error) {
      console.error("Ошибка загрузки лидов:", error);
      toast.error(`Ошибка загрузки: ${error.message}`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = () => {
    loadLeads(false);
  };

  const handleApplyFilters = () => {
    loadLeads(false);
  };

  const handleResetFilters = () => {
    setSelectedEvents([]);
    setSelectedLeadTypes([]);
    setSelectedTrafficSources([]);
    setSelectedWorksWithUs([]);
    setSelectedCommissionModels([]);
    setSelectedManagers([]);
    setSearchQuery("");
  };

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    
    // Загружаем больше когда до конца осталось 200px
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadLeads(true);
    }
  }, [loadingMore, hasMore, offset]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <Section
      title={`Все лиды${total > 0 ? ` (${total})` : ''}`}
      right={
        <ToolbarButton onClick={() => setView("home")}>Назад</ToolbarButton>
      }
    >
      {/* Поиск */}
      <div className="mb-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Поиск по имени, контакту, компании, заметкам..."
            className="flex-1 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 outline-none transition-colors"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium"
          >
            Найти
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="space-y-2 mb-3">
        <MultiSelect
          options={events.map(e => ({ value: e.code, label: e.name }))}
          value={selectedEvents}
          onChange={setSelectedEvents}
          placeholder="События"
        />

        <div className="grid md:grid-cols-2 gap-2">
          <MultiSelect
            options={leadTypes.map(t => ({ value: t, label: t }))}
            value={selectedLeadTypes}
            onChange={setSelectedLeadTypes}
            placeholder="Тип лида"
          />

          <MultiSelect
            options={trafficSources.map(s => ({ value: s, label: s }))}
            value={selectedTrafficSources}
            onChange={setSelectedTrafficSources}
            placeholder="Источник трафика"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-2">
          <MultiSelect
            options={worksWithUsOptions.map(w => ({ value: w.value, label: w.label }))}
            value={selectedWorksWithUs}
            onChange={setSelectedWorksWithUs}
            placeholder="Статус работы"
          />

          <MultiSelect
            options={commissionModels.map(m => ({ value: m, label: m }))}
            value={selectedCommissionModels}
            onChange={setSelectedCommissionModels}
            placeholder="Модель комиссии"
          />
        </div>

        <MultiSelect
          options={managers.map(m => ({
            value: m.telegram_id,
            label: `${m.username || 'Без username'} (${m.full_name || 'Без имени'})`
          }))}
          value={selectedManagers}
          onChange={setSelectedManagers}
          placeholder="Менеджеры"
        />

        <div className="flex gap-2">
          <button
            onClick={handleApplyFilters}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium"
          >
            Показать результат
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Сбросить
          </button>
        </div>
      </div>

      {/* Список лидов */}
      <div 
        ref={scrollContainerRef}
        className="max-h-[60vh] overflow-y-auto"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Загрузка лидов...</p>
            </div>
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Лидов не найдено. Попробуйте изменить фильтры.
          </div>
        ) : (
          <>
            <div className="grid gap-3 mb-4">
              {leads.map((lead) => {
                // Преобразуем фото в формат photoDataUrls для LeadCard
                const leadWithPhotos = {
                  ...lead,
                  // Преобразуем snake_case в camelCase для совместимости с LeadCard
                  id: lead.id,
                  managerTg: lead.manager_username ? `@${lead.manager_username}` : `user_${lead.manager_telegram_id}`,
                  managerName: `${lead.manager_name || 'Без имени'} (${lead.manager_username || lead.manager_telegram_id})`,
                  event: lead.event,
                  name: lead.name,
                  company: lead.company,
                  contact: lead.contact,
                  leadType: lead.lead_type,
                  customLeadType: lead.custom_lead_type,
                  trafficSource: lead.traffic_source,
                  customTraffic: lead.custom_traffic_source,
                  worksWithUs: lead.works_with_us,
                  commissionModel: lead.commission_model,
                  customCommission: lead.custom_commission_model,
                  notes: lead.notes,
                  createdAt: lead.created_at,
                  updatedAt: lead.updated_at,
                  photoDataUrls: lead.photos?.map(p => p.url) || [],
                  // New fields
                  status: lead.status || "to_process",
                  priority: lead.priority,
                  partner_id: lead.partner_id,
                  contact_telegram: lead.contact_telegram,
                  contact_whatsapp: lead.contact_whatsapp,
                  contact_wechat: lead.contact_wechat,
                  contact_email: lead.contact_email,
                  contact_phone: lead.contact_phone,
                };
                
                return (
                  <LeadCard 
                    key={lead.id}
                    lead={leadWithPhotos} 
                    onEdit={null}
                    onDelete={null}
                    readOnly={true}
                    showManager={true}
                  />
                );
              })}
            </div>

            {/* Индикатор загрузки следующей партии */}
            {loadingMore && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-400">Загрузка...</span>
              </div>
            )}

            {/* Конец списка */}
            {!hasMore && leads.length > 0 && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                Все лиды загружены ({total})
              </div>
            )}
          </>
        )}
      </div>
    </Section>
  );
}
